// src/pages/VocationalSecundaryResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/VocationalSecundaryResults.css";
import logoMirai from "../assets/mirai_logo.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// === Estilos PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results, recommendations }) => {
  const date = new Date().toLocaleDateString();
  const rec = recommendations[results.bestArea];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Orientación Vocacional</Text>
          <Text>Nivel Medio Superior</Text>
        </View>

        {/* Datos */}
        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>Área con mayor afinidad: {results.bestArea} ({results.bestScore} pts)</Text>
        </View>

        {/* Resumen por área */}
        <Text style={styles.sectionTitle}>Resumen por Área:</Text>
        {Object.entries(results.totalScores || {}).map(([area, data]) => (
          <View key={area} style={styles.card}>
            <Text>{area} – {data.level} ({data.score} pts)</Text>
          </View>
        ))}

        {/* Carreras sugeridas */}
        {rec && (
          <>
            <Text style={styles.sectionTitle}>Carreras Técnicas Sugeridas:</Text>
            <View style={styles.card}>
              {rec.careers.map((c, idx) => (
                <Text key={idx} style={styles.recommendation}>• {c}</Text>
              ))}
            </View>

            <Text style={styles.sectionTitle}>Instituciones que las Ofrecen:</Text>
            <View style={styles.card}>
              {rec.schools.map((s, idx) => (
                <Text key={idx} style={styles.recommendation}>• {s}</Text>
              ))}
            </View>
          </>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const VocationalSecundaryResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const navigate = useNavigate();

  const vocationalRecommendations = {
    "Ciencia y Tecnología": {
      careers: [
        "Técnico en Sistemas Computacionales / Informática",
        "Técnico en Programación y Desarrollo de Software",
        "Técnico en Redes y Telecomunicaciones",
        "Técnico en Electrónica",
        "Técnico en Mecatrónica y Automatización Industrial",
        "Técnico en Inteligencia Artificial y Robótica",
        "Técnico en Energías Renovables",
        "Técnico en Nanotecnología y Ciberseguridad",
        "Técnico en Biotecnología",
        "Técnico en Matemáticas Aplicadas y Estadística",
        "Técnico en Bioinformática",
        "Técnico en Tecnologías de la Información",
      ],
      schools: ["CONALEP", "CECyT del IPN", "Preparatorias Técnicas", "UNITEC, La Salle, UVM", "Tecnológicos Nacionales de México", "UANL, UDG, BUAP, UABC"]
    },
    "Ciencias Sociales": {
      careers: [
        "Técnico en Gestión Administrativa y de Empresas",
        "Técnico en Contaduría y Finanzas",
        "Técnico en Comercio Internacional y Logística",
        "Técnico en Mercadotecnia y Publicidad",
        "Técnico en Relaciones Internacionales",
        "Técnico en Comunicación y Periodismo",
        "Técnico en Trabajo Social y Gestión Cultural",
        "Técnico en Administración Pública",
        "Técnico en Gestión de Proyectos",
      ],
      schools: ["CONALEP", "Preparatorias Técnicas", "CECyT del IPN", "La Salle, UVM, UNITEC", "Tecnológicos Nacionales de México", "UANL, UDG, BUAP"]
    },
    "Arte y Diseño": {
      careers: [
        "Técnico en Diseño Gráfico y Multimedia",
        "Técnico en Diseño de Interiores",
        "Técnico en Diseño Industrial",
        "Técnico en Animación Digital y Arte Digital",
        "Técnico en Producción Audiovisual y Fotografía",
        "Técnico en Publicidad y Comunicación Visual",
        "Técnico en Moda y Diseño Textil",
        "Técnico en Escenografía y Diseño de Producto",
      ],
      schools: ["CONALEP", "Preparatorias Técnicas Especializadas", "CECyT del IPN", "La Salle, UVM, UNITEC", "Tecnológicos Nacionales de México", "UDG, UANL"]
    },
    "Ciencias de la Salud": {
      careers: [
        "Técnico en Enfermería",
        "Técnico en Laboratorio Clínico e Imagenología",
        "Técnico en Nutrición y Salud",
        "Técnico en Farmacia y Química Farmacéutica",
        "Técnico en Medicina Prehospitalaria y Emergencias",
        "Técnico en Terapia Ocupacional",
        "Técnico en Kinesiología",
        "Técnico en Terapia Respiratoria",
      ],
      schools: ["CONALEP", "Preparatorias Técnicas", "CECyT del IPN", "La Salle, UVM, UNITEC", "Tecnológicos Nacionales de México", "UANL, UDG, BUAP"]
    },
    "Empresarial": {
      careers: [
        "Técnico en Administración y Gestión Empresarial",
        "Técnico en Contaduría y Finanzas",
        "Técnico en Comercio Internacional y Logística",
        "Técnico en Mercadotecnia y Publicidad",
        "Técnico en Gestión de Recursos Humanos",
        "Técnico en Emprendimiento y Desarrollo Empresarial",
        "Técnico en E-commerce y Gestión Comercial",
        "Técnico en Administración Hotelera",
        "Técnico en Gestión de Innovación",
      ],
      schools: ["CONALEP", "Preparatorias Técnicas", "CECyT del IPN", "La Salle, UVM, UNITEC", "Tecnológicos Nacionales de México", "UANL, UDG, BUAP"]
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setResults(response.data.VocationalSecundaryResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header"><h3>Orientación Vocacional</h3></div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "overview" ? "active" : ""}`}
              onClick={() => setActiveSection("overview")}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>
            <button className="menu-item return-dashboard" onClick={() => navigate("/dashboard")}>
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="dashboard-main">
          <div className="results-content">
            {loading ? (
              <p className="text-center mt-5">Cargando resultados...</p>
            ) : !results || !results.totalScores ? (
              <p className="text-center mt-5">No se encontraron resultados vocacionales.</p>
            ) : (
              <>
                <h3 className="text-center mb-4">
                  Resultados de Orientación Vocacional Nivel Medio Superior
                </h3>
                <p className="text-muted text-center">
                  Esta evaluación analiza tu afinidad con cinco áreas profesionales.
                </p>

                {/* Área con mejor afinidad */}
                <div className="text-center mb-4">
                  <h4>Área con mayor afinidad:</h4>
                  <div className="alert alert-success fs-5 fw-bold mt-2">
                    {results.bestArea} ({results.bestScore} puntos)
                  </div>
                </div>

                {/* Resultados por área */}
                <div className="skills-summary">
                  <h5 className="mb-3">Resumen por Área</h5>
                  <div className="skills-chart">
                    {Object.entries(results.totalScores).map(([area, data]) => (
                      <div key={area} className="chart-bar mb-3">
                        <div className="bar-label">
                          <strong>{area}</strong> – {data.level}
                        </div>
                        <div className="bar-container">
                          <div
                            className="bar-fill"
                            style={{
                              width: `${(data.score / 36) * 100}%`,
                              backgroundColor:
                                data.level === "Alta Afinidad"
                                  ? "#28a745"
                                  : data.level === "Afinidad Moderada"
                                    ? "#ffc107"
                                    : "#dc3545",
                            }}
                          >
                            {data.score} pts
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Carreras y escuelas */}
                {results.bestArea && vocationalRecommendations[results.bestArea] && (
                  <div className="career-recommendations mt-5">
                    <h4 className="text-center mb-4 text-primary fw-bold">
                      <i className="bi bi-star-fill text-warning me-2"></i>
                      Recomendaciones para tu perfil
                    </h4>
                    <div className="recommendation-box bg-white p-4 rounded shadow-sm border mb-4">
                      <h5 className="mb-3 text-secondary fw-bold border-bottom pb-2">
                        <i className="bi bi-briefcase-fill me-2 text-info"></i>
                        Carreras Técnicas Sugeridas
                      </h5>
                      <div className="d-flex flex-wrap gap-2 mb-5">
                        {vocationalRecommendations[results.bestArea].careers.map((career, i) => (
                          <span key={i} className="badge rounded-pill bg-primary fw-normal p-2 fs-6 shadow-sm">
                            {career}
                          </span>
                        ))}
                      </div>

                      <h5 className="mb-3 text-secondary fw-bold border-bottom pb-2">
                        <i className="bi bi-building-fill me-2 text-danger"></i>
                        Instituciones que las Ofrecen
                      </h5>
                      <div className="row g-3">
                        {vocationalRecommendations[results.bestArea].schools.map((school, i) => (
                          <div key={i} className="col-md-6 col-lg-6">
                            <div className="card shadow-sm h-100 border-0 bg-light">
                              <div className="card-body d-flex align-items-center">
                                <i className="bi bi-mortarboard-fill text-secondary fs-3 me-3"></i>
                                <p className="card-text mb-0 small text-dark fw-medium lh-sm">
                                  {school}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* PDF */}
                <div className="results-actions text-center mt-4">
                  <PDFDownloadLink
                    document={<ResultsPDF user={user} results={results} recommendations={vocationalRecommendations} />}
                    fileName={`Resultados_Vocacionales_${new Date().toLocaleDateString().replaceAll("/", "_")}.pdf`}
                  >
                    {({ loading }) =>
                      loading ? (
                        <button className="btn btn-secondary">Generando PDF...</button>
                      ) : (
                        <button className="btn btn-primary">
                          <i className="bi bi-download"></i> Descargar Resultados
                        </button>
                      )
                    }
                  </PDFDownloadLink>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VocationalSecundaryResults;
